import type { Enduser, FormField } from "@tellescope/types-models";
import type { TellescopeModelFromName } from "@tellescope/tanstack-query";
import { TellescopeFormField } from "../tellescope-form-field";
import { useResolveRedirectTarget, type ContinueToFormInApp } from "../useResolveRedirectTarget";
import type { TellescopeFieldResponse } from "../types";
import type { QuestionGroupFieldProps } from "./QuestionGroupField";

export interface QuestionGroupRedirectSubFieldProps {
  subField: TellescopeModelFromName<"form_fields">;
  getFieldProps: QuestionGroupFieldProps["getFieldProps"];
  responsesArray: TellescopeFieldResponse[];
  isStaffSession: boolean;
  enduser?: TellescopeModelFromName<"endusers"> | Enduser;
  enduserId?: string;
  fieldsArr: (Pick<FormField, "intakeField"> & { id: string })[];
  submitForm?: () => Promise<unknown>;
  isResponseSubmitted?: boolean;
  formResponseId?: string;
  rootResponseId?: string;
  documentGroupBackUrl?: string;
  continueToFormInApp?: ContinueToFormInApp;
}

/**
 * A `Redirect` sub-field inside a Question Group. It exists as its own component because the
 * redirect resolver is built from the field's own `options`, so a nested Redirect needs a resolver
 * keyed to the sub-field rather than the group that contains it.
 */
export const QuestionGroupRedirectSubField = ({
  subField,
  getFieldProps,
  responsesArray,
  isStaffSession,
  enduser,
  enduserId,
  fieldsArr,
  submitForm,
  isResponseSubmitted,
  formResponseId,
  rootResponseId,
  documentGroupBackUrl,
  continueToFormInApp,
}: QuestionGroupRedirectSubFieldProps) => {
  const resolveTarget = useResolveRedirectTarget({
    options: subField.options,
    enduser,
    enduserId: enduserId ?? "",
    responses: responsesArray,
    fieldsArr,
    submitForm: submitForm ?? (() => Promise.resolve()),
    isSubmitted: !!isResponseSubmitted,
    formResponseId,
    rootResponseId,
    documentGroupBackUrl,
    continueToFormInApp,
  });

  const props = getFieldProps(subField.id, "Redirect");
  if (!props) return null;

  return <TellescopeFormField<"Redirect"> {...props} isStaffSession={isStaffSession} resolveTarget={resolveTarget} />;
};
